// Roary, the BuildLab lion (decisions 60 and 69): CHA's lion as an 8-bit sprite, in the explainer kit's palette.
// One character per pixel, "." is see-through. A pose is a stack of layers (x, y, rows) drawn in
// order on a 30×26 grid, so the head is shared and only the arms change. Not the official CHA logo.

export const LION_PALETTE: Record<string, string> = {
  K: "#020c1f", // outline
  M: "#b8791f", // mane
  m: "#d9962f", // mane highlight
  G: "#f0ad42", // CHA Gold: face, arms
  Y: "#ffd98a", // muzzle, paws
  W: "#ffffff", // eye shine
  S: "#d46048", // CHA Salmon: cheeks
  s: "#9e3f2c", // nose
  B: "#134b8d", // CHA Medium Blue: shirt
  N: "#041e42", // CHA Navy: shirt letter
};

export const LION_WIDTH = 30;
export const LION_HEIGHT = 26;

type Layer = [x: number, y: number, rows: string[]];

const HEAD: string[] = [
  ".......KKK....KKK.......",
  "......KGYGK..KGYGK......",
  "....KKMMMMMKKMMMMMKK....",
  "..KMMMmMMMMMMMMMMmMMMK..",
  "..KMMMMKKKKKKKKKKMMMMK..",
  ".KMMmMKGGGGGGGGGGKMmMMK.",
  ".KMMMKGGGGGGGGGGGGKMMMK.",
  "KMMMKGGWKGGGGGGWKGGKMMMK",
  ".KMmKGGKKGGGGGGKKGGKmMK.",
  ".KMMKGGGGGGYYGGGGGGKMMK.",
  "KMMMKGSSGYYssYYGSSGKMMMK",
  ".KMMKGGGYYYYYYYYGGGKMMK.",
  "..KMMKGGYKYYYYKYGGKMMK..",
  "..KMMKGYYYKKKKYYYGKMMK..",
  "..KMMMMKKGGGGGGKKMMMMK..",
  "....KKMMMKKKKKKMMMKK....",
  ".......KKKKKKKKKK.......",
];

// Shirt in CHA blue with a gold "R" (Roary, decision 69), gold legs, light feet. The R is the
// classic 3×5 bitmap letter, the widest that fits the shirt front.
const BODY: string[] = [
  "..KBBBBBBK..",
  "..KBGGBBBK..",
  ".KBBGBGBBBK.",
  ".KBBGGBBBBK.",
  ".KBBGBGBBBK.",
  "..KBGBGBBK..",
  "..KGGKKGGK..",
  ".KYYYKKYYYK.",
  ".KKKKKKKKKK.",
];

const ARM_DOWN_L: string[] = [".KK", "KGK", "KGK", "KGK", "KYK", ".K."];
const ARM_DOWN_R: string[] = ["KK.", "KGK", "KGK", "KGK", "KYK", ".K."];

// Raised arm on the lion's left (the viewer's right), paw up beside the mane.
const ARM_UP_R: string[] = [
  ".......KKK.",
  "......KYYYK",
  "......KYYYK",
  ".......KGK.",
  ".......KGK.",
  ".......KGK.",
  ".......KGK.",
  "......KGK..",
  "......KGK..",
  ".....KGK...",
  "....KGK....",
  "...KGK.....",
  "..KGK......",
  ".KGK.......",
];
const ARM_UP_L: string[] = ARM_UP_R.map((r) => [...r].reverse().join(""));

// A paw under the cheek, for thinking.
const PAW_CHIN: string[] = [".KKK.", "KYYYK", "KYYYK", ".KGK.", ".KGK.", "..KGK", "..KGK"];

const head: Layer = [3, 0, HEAD];
const body: Layer = [9, 16, BODY];

export const LION_POSES = {
  /** Standing, arms down. */
  stand: [body, [7, 17, ARM_DOWN_L], [20, 17, ARM_DOWN_R], head],
  /** Waving hello (Home, sign-in). */
  wave: [body, [7, 17, ARM_DOWN_L], [19, 5, ARM_UP_R], head],
  /** Thinking (loading, empty states). */
  think: [body, [7, 17, ARM_DOWN_L], head, [17, 12, PAW_CHIN]],
  /** Both arms up (done, celebrations). */
  cheer: [body, [0, 5, ARM_UP_L], [19, 5, ARM_UP_R], head],
  /** Just the head, for small places like the top bar. */
  head: [[0, 0, HEAD]],
} satisfies Record<string, Layer[]>;

export type LionPose = keyof typeof LION_POSES;

/** Flattens a pose into one row of pixels per line ("." where nothing is drawn). */
export function lionGrid(pose: LionPose): string[] {
  const layers = LION_POSES[pose];
  const w = pose === "head" ? HEAD[0]!.length : LION_WIDTH;
  const h = pose === "head" ? HEAD.length : LION_HEIGHT;
  const grid = Array.from({ length: h }, () => Array<string>(w).fill("."));
  for (const [x, y, rows] of layers) {
    rows.forEach((row, dy) => {
      [...row].forEach((ch, dx) => {
        if (ch !== "." && grid[y + dy] && x + dx < w) grid[y + dy]![x + dx] = ch;
      });
    });
  }
  return grid.map((r) => r.join(""));
}

/** Horizontal runs of one colour, so the SVG has a few dozen rects instead of hundreds. */
export function lionRuns(pose: LionPose): { x: number; y: number; w: number; c: string }[] {
  const runs: { x: number; y: number; w: number; c: string }[] = [];
  lionGrid(pose).forEach((row, y) => {
    let x = 0;
    while (x < row.length) {
      const ch = row[x]!;
      let end = x + 1;
      while (end < row.length && row[end] === ch) end++;
      if (ch !== ".") runs.push({ x, y, w: end - x, c: LION_PALETTE[ch] ?? "#f0f" });
      x = end;
    }
  });
  return runs;
}
